// src/lib/reactKey.ts
// Stable key helper: prevents duplicate keys when callers pass objects.
// - If value is string/number: uses it
// - If value is object: uses id/key if present, else WeakMap-stable uid
// - Always produces unique keys within a scope

const _objIdsByScope: Record<string, WeakMap<object, string>> = {};
const _countersByScope: Record<string, number> = {};

function getScopeMap(scope: string) {
  if (!_objIdsByScope[scope]) _objIdsByScope[scope] = new WeakMap();
  if (typeof _countersByScope[scope] !== "number") _countersByScope[scope] = 0;
  return _objIdsByScope[scope];
}

function nextScopeId(scope: string) {
  _countersByScope[scope] += 1;
  return `${_countersByScope[scope]}`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object";
}

export function safeKey(scope: string, value: unknown, fallbackIndex?: number): string {
  // Fast path: primitives
  if (typeof value === "string" && value.length) return `k:${scope}-${value}`;
  if (typeof value === "number" && Number.isFinite(value)) return `k:${scope}-${value}`;

  // Objects: prefer common identity fields
  if (isRecord(value)) {
    const id =
      (typeof value.id === "string" && value.id) ||
      (typeof value.key === "string" && value.key) ||
      (typeof value._id === "string" && value._id) ||
      (typeof value.name === "string" && value.name) ||
      "";

    if (id) return `k:${scope}-${id}`;

    // WeakMap-stable identity per object reference
    const map = getScopeMap(scope);
    const existing = map.get(value as object);
    if (existing) return `k:${scope}-${existing}`;

    const uid = `obj-${nextScopeId(scope)}`;
    map.set(value as object, uid);
    return `k:${scope}-${uid}`;
  }

  // Fallback: index if provided, else hard-unique per scope tick
  if (typeof fallbackIndex === "number" && Number.isFinite(fallbackIndex)) {
    return `k:${scope}-i${fallbackIndex}`;
  }
  return `k:${scope}-u${nextScopeId(scope)}`;
}
